import { toast } from 'sonner';
import { useMutation } from '@apollo/client/react';

import { type IEscrowPartyParams } from 'src/graphql/schema';
import { BeneficiaryCreateDocument } from 'src/graphql/actions/beneficiary/beneficiaryCreate.mutation';
import { BeneficiaryUpdateDocument } from 'src/graphql/actions/beneficiary/beneficiaryUpdate.mutation';
import { updateBeneficiaryCache } from 'src/graphql/client/cache';
import { ClientError, getApiError } from 'src/utils/errors';

import { useBeneficiaryContext } from './BeneficiaryContext';

// An escrow has a single beneficiary: the first save creates it, every later save (edit or change) updates it.
// Resolves to whether it was saved; on success the card returns to the details, on failure an error toast is shown.
export const useSaveBeneficiary = () => {
  const { beneficiary, escrowId, setMode } = useBeneficiaryContext();
  const [createBeneficiary] = useMutation(BeneficiaryCreateDocument);
  const [updateBeneficiary] = useMutation(BeneficiaryUpdateDocument, {
    update: (_, { data }) => updateBeneficiaryCache(escrowId, data?.beneficiaryUpdate?.beneficiary),
  });

  return async (beneficiaryParams: IEscrowPartyParams) => {
    try {
      if (beneficiary) {
        const { data } = await updateBeneficiary({ variables: { beneficiaryId: beneficiary.id, beneficiaryParams } });

        if (data?.beneficiaryUpdate?.errors?.length) throw new ClientError(data.beneficiaryUpdate.errors[0]);

        toast.success('Details updated!');
      } else {
        const { data } = await createBeneficiary({ variables: { escrowId, beneficiaryParams } });

        if (data?.beneficiaryCreate?.errors?.length) throw new ClientError(data.beneficiaryCreate.errors[0]);

        toast.success('Beneficiary Organization Details added!');
      }

      setMode('view');

      return true;
    } catch (e: unknown) {
      getApiError(e, toast.error);

      return false;
    }
  };
};
